import { useMiniApp } from '@app/providers/miniapp-provider'
import { useLastAppStateStore } from '@app/providers/router/model/store/last-app-state-store'
import { useChannelStore } from '@entities/channel'
import { login, loginAnonymous, loginByMiniApp, refreshTokens } from '@shared/auth/api'
import type { LoginArgs, LoginMiniAppArgs, RefreshArgs } from '@shared/auth/api/types'
import { useAuthStore } from '@shared/auth/model/store/auth-store'
import { useJWTTokensStore } from '@shared/auth/model/store/jwt-tokens'
import { getErrorMessage } from '@shared/lib/errors'
import { App } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

export const useAuth = () => {
    const [error, setError] = useState('')

    const navigate = useNavigate()

    const { message } = App.useApp()

    const { isMiniApp, telegramWebApp } = useMiniApp()

    const {
        refresh: rToken,
        setAccess,
        setRefresh
    } = useJWTTokensStore(useShallow(({ refresh, setAccess, setRefresh }) => ({ refresh, setAccess, setRefresh })))

    const { isAuth, isLoading, setIsAuth, setIsLoading } = useAuthStore(
        useShallow(({ isAuth, isLoading, setIsAuth, setIsLoading }) => ({
            isAuth,
            isLoading,
            setIsAuth,
            setIsLoading
        }))
    )

    const { setCurrentChannel } = useChannelStore(useShallow(({ setCurrent }) => ({ setCurrentChannel: setCurrent })))

    const onAuthSuccess = useCallback(
        (access: string, refresh: string) => {
            setAccess(access)
            setRefresh(refresh)
            setIsAuth(true)
            setIsLoading(false)
        },
        [setAccess, setRefresh, setIsAuth, setIsLoading]
    )

    const loginByTelegram = useCallback(
        async (data: LoginArgs) => {
            try {
                const { refresh, access } = await login(data)
                onAuthSuccess(access, refresh)
                navigate('/')
            } catch (error) {
                message.error(getErrorMessage(error))
            }
        },
        [message, navigate, onAuthSuccess]
    )

    const loginForAnonymous = useCallback(async () => {
        try {
            const { refresh, access } = await loginAnonymous()
            onAuthSuccess(access, refresh)
            navigate('/')
        } catch (error) {
            message.error(getErrorMessage(error))
        }
    }, [message, navigate, onAuthSuccess])

    const loginByTgMiniApp = useCallback(
        async (data: LoginMiniAppArgs) => {
            try {
                const { refresh, access } = await loginByMiniApp(data)
                onAuthSuccess(access, refresh)
                setCurrentChannel(null)

                const page = useLastAppStateStore.getState().page

                if (page) {
                    navigate(page)
                } else {
                    navigate('/')
                }
            } catch (error) {
                alert(error)
                message.error(getErrorMessage(error))
            }
        },
        [message, navigate, onAuthSuccess, setCurrentChannel]
    )

    const refreshToken = useCallback(
        async (data: RefreshArgs, onError?: () => void) => {
            try {
                const { access, refresh } = await refreshTokens(data)
                setAccess(access)
                setRefresh(refresh)
                setIsAuth(true)
                setIsLoading(false)
            } catch (error) {
                setError(getErrorMessage(error))
                setIsAuth(false)
                onError?.()
            } finally {
                setIsLoading(false)
            }
        },
        [setAccess, setIsAuth, setIsLoading, setRefresh]
    )

    useEffect(() => {
        if (isLoading) {
            if (isMiniApp && telegramWebApp.initData) {
                loginByTgMiniApp({ initData: telegramWebApp.initData })
            } else {
                refreshToken({ refresh: rToken })
            }
        }
    }, [])

    return {
        isAuth,
        error,
        isLoading,
        loginByTelegram,
        loginForAnonymous,
        loginByTgMiniApp
    }
}
